<template>
  <CardHeaderTips main-text="准备分析" normal-text="，请选择提炼模版" style="margin-left: -12px" />
  <div class="meeting-card">
    <!-- 选择场景部分 -->
    <div v-if="attributes.meetingRuleShowTag !== 1" class="scene-selection">
      <div class="selection-label">
        {{ [1, 2].includes(meetingRuleShowTag) && ruleId ? '已自动选用唯一可用场景' : '提炼模板' }}
      </div>
      <template v-if="isMobile()">
        <MobileSelectTrigger
          :columns="rulePickerColumns"
          :disabled="disabled"
          :label="selectedRuleLabel"
          :picker-value="rulePickerValue"
          title="选择提炼模板"
          @confirm="handleRulePickerConfirm"
        />
      </template>
      <el-select
        v-else
        v-model="ruleId"
        :disabled="disabled"
        placeholder="请选择"
        @change="handleRuleChange"
      >
        <el-option
          v-for="item in rules"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { getSummaryRuleList, createTask, getSseMsg } from '../../api'
import LoadingCard from '@/components/Cards/LoadingCard.vue'
import CardHeaderTips from '@/components/Cards/components/card-header-tips.vue'
import MobileSelectTrigger from '@/components/MobileSelectTrigger.vue'
import { isMobile } from '@/utils'

const ruleId = ref('')
const rules = ref([])
const disabled = ref(false)
const selectedRuleLabel = computed(
  () => rules.value.find((item) => `${item.value}` === `${ruleId.value}`)?.label || ''
)
const rulePickerColumns = computed(() =>
  rules.value.map((item) => ({ text: item.label, value: item.value }))
)
const rulePickerValue = computed(() =>
  ruleId.value === undefined || ruleId.value === null || ruleId.value === '' ? [] : [ruleId.value]
)
/**
 * 模板展示规则
 * 0 历史无操作数据：存在模板数据，展示选择器，不做默认操作；
 * 1 老版本兜底规则：存在默认模板数据，不展示选择器，调用分析接口；
 * 2 现版本单一模板：仅存在一个模板数据，展示选择器，默认选择且不可操作并调用分析接口；
 */
const meetingRuleShowTag = ref(1)

const emit = defineEmits(['scrollEnd', 'changeExtractStatus', 'addMessage', 'refreshList'])

const props = defineProps({
  fileUrl: {
    type: String
  },
  attributes: {
    type: Object
  },
  recordId: {
    type: Number
  }
})

onMounted(async () => {
  const _ruleId = props.attributes?.id
  if (_ruleId) {
    // 存在id，表明为历史数据，直接赋值渲染
    ruleId.value = _ruleId
    rules.value = [
      {
        value: _ruleId,
        label: props.attributes?.name || '--'
      }
    ]
    disabled.value = true
  } else {
    // 不存在id，表明为新数据，需要获取模板列表
    const { result } = await getSummaryRuleList()
    if (result) {
      rules.value = result.dataList.map((item) => ({
        value: item.id,
        label: item.name
      }))
      meetingRuleShowTag.value = result.meetingRuleShowTag
      if (
        !props.attributes?.id &&
        [1, 2].includes(result.meetingRuleShowTag) &&
        result.dataList.length &&
        props.attributes?.meetingRuleShowTag
      ) {
        ruleId.value = result.dataList[0]?.id
        disabled.value = true
        // eslint-disable-next-line vue/no-mutating-props
        props.attributes.meetingRuleShowTag = result.meetingRuleShowTag
        handleRuleChange(ruleId.value)
      }
    }
  }
})

const handleRulePickerConfirm = ({ selectedValues }) => {
  const value = selectedValues?.[0]
  if (value === undefined) return
  // AI修复 避免移动端在 VoiceCard 里重复确认同一模板时再次触发分析
  if (`${value}` === `${ruleId.value}`) return
  ruleId.value = value
  handleRuleChange(value)
}

const handleRuleChange = (value) => {
  emit('scrollEnd')
  createTask({
    fileUrl: props.attributes?.fileUrl || props.fileUrl,
    ruleId: value,
    chatInfo: {
      id: ruleId.value,
      name: rules.value.find((item) => item.value === ruleId.value)?.label
    },
    messageSourceType: 2,
    chatTemplateTag: 2,
    chatRecordId: props.attributes.chatRecordId,
    meetingRuleShowTag: meetingRuleShowTag.value,
    recordId: props.attributes.recordId || props.recordId
  })
    .then(async (res) => {
      disabled.value = true
      emit('changeExtractStatus', 1)
      emit('addMessage', {
        type: 'component',
        id: res.result?.taskId,
        extractStatus: 1,
        componentName: 'LoadingCard',
        role: 'assistant',
        content: {
          component: LoadingCard,
          attributes: {
            assistantId: 2,
            isMedicalIndustry: props.attributes?.isMedicalIndustry
          }
        }
      })
      getSseMsg({ chatRecordId: res.result.chatRecordId })
        .finally(() => {
          emit('refreshList', res.result.chatRecordId)
        })
        .catch((err) => {
          console.log(err, 'getSseMsg')
        })
    })
    .catch((err) => {
      console.log(err)
      // ruleId.value = ''
      disabled.value = false
    })
}
</script>

<style lang="less" scoped>
.text-color-main {
  color: #323233;
}
.text-color-normal {
  color: #969799;
}

.tips {
  display: flex;
  align-items: center;
  margin-bottom: 18px;

  &-text {
    font-family: PingFang SC;
    font-size: 14px;
    font-weight: normal;
    line-height: normal;
    letter-spacing: 0em;
    color: #969799;
    margin-left: 8px;
  }
}

.btn {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.meeting-card {
  width: 100%;
  max-width: 400px;
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans',
    'Helvetica Neue', sans-serif;
  color: #333;
  border-radius: 8px;
  background: #fff;
  padding: 12px;
  margin: -12px;

  .card-header {
    margin-bottom: 16px;

    .header-item {
      display: flex;
      align-items: center;
      color: #969799;

      .header-text {
        font-size: 14px;
        margin-left: 2px;
      }
    }
  }

  .scene-selection {
    margin-bottom: 24px;

    .selection-label {
      color: #323233;
      font-size: 14px;
      margin-bottom: 8px;
    }

    .el-dropdown {
      width: 100%;

      .dropdown-button {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 6px 12px;
        border: 1px solid #e5e6eb;
        border-radius: 4px;
        cursor: pointer;

        &.disabled {
          cursor: not-allowed;
          background: #f7f8fa;
        }
      }
    }

    :deep(.selection-popper) {
      width: 100%;
    }
  }

  .task-creation {
    .task-title {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 8px;
      color: #323233;
    }

    .status-item {
      display: flex;
      align-items: center;
      margin-bottom: 4px;
      color: #969799;

      .status-text {
        font-size: 15px;
        margin-left: 2px;
      }
    }

    .processing-status {
      .processing-text {
        font-size: 14px;
        color: #323233;
      }
    }
  }
}
</style>
